import type { FC, PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { localStorageHelper } from '@utils/localStorageHelper'
import { GUEST_CUSTOMER_HEADER } from '@utils/axios-utils'
import { getWsCookies } from '@hooks/useSso'

let guestCustomerIdFetchPromise: Promise<string> | null = null

const ensureGuestCustomerId = async (): Promise<string> => {
  const existingId = localStorageHelper.get(GUEST_CUSTOMER_HEADER)
  if (existingId) {
    return existingId
  }

  if (guestCustomerIdFetchPromise) {
    return guestCustomerIdFetchPromise
  }

  guestCustomerIdFetchPromise = axios
    .get('/api/guest-customer')
    .then((result: AxiosResponse<{ customerId: string }>) => {
      const customerId = result.data.customerId.toString()
      localStorageHelper.set(GUEST_CUSTOMER_HEADER, customerId)
      guestCustomerIdFetchPromise = null // Clear the promise after completion
      return customerId
    })
    .catch((error) => {
      guestCustomerIdFetchPromise = null // Clear the promise on error
      throw error
    })

  return guestCustomerIdFetchPromise
}

export const AxiosConfig: FC<PropsWithChildren> = ({ children }) => {
  const [guestCustomerId, setGuestCustomerId] = useState<string>(localStorageHelper.get(GUEST_CUSTOMER_HEADER))

  useEffect(() => {
    if (!guestCustomerId) {
      ensureGuestCustomerId().then((customerId) => {
        setGuestCustomerId(customerId)
      })
    }
  }, [setGuestCustomerId, guestCustomerId])

  useEffect(() => {
    const ejectId = axios.interceptors.request.use(async (request: InternalAxiosRequestConfig<any>) => {
      const { sessionId } = getWsCookies()
      const isAuthorized = localStorageHelper.get('ssoToken')
      const isLoggingIn = typeof window !== 'undefined' && window.__isLoggingIn__

      if (!sessionId && isAuthorized && isLoggingIn) {
        window.SSO?.signOut?.()
      }

      if (!request.url?.includes('/api/guest-customer')) {
        const currentGuestCustomerId = await ensureGuestCustomerId()
        request.headers[GUEST_CUSTOMER_HEADER] = currentGuestCustomerId
      }

      return request
    })
    const ejectIdRes = axios.interceptors.response.use((response) => {
      return response
    })

    return () => {
      axios.interceptors.request.eject(ejectId)
      axios.interceptors.response.eject(ejectIdRes)
    }
  }, [])

  return <>{children}</>
}

export default AxiosConfig
